import { centsToFormattedDollars } from '@fruitful/fruitils';
import {
    Account,
    type BankAccountDisplayDetail,
    formatIncomeSplitPercentagesNew,
    IncomeSplitDetails,
    IncomeSplitProgressOverview,
    IncomeSplitRulesListNew,
    PaycheckRoutingStatus,
} from '@fruitful/income-split';
import IncomeSplitEndOfRules from '@fruitful/income-split/src/components/IncomeSplitEndOfRules';
import { useAtomValue } from 'jotai';
import { Badge } from 'primereact/badge';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useMemo, useState } from 'react';
import {
    AlertCircleIcon,
    BodyLargeEmphasized,
    BodyMediumEmphasized,
    Button,
    ButtonSize,
    ButtonVariant,
    CodeIcon,
    EyeIcon,
    Icon,
    IconButton,
    MoneyMapIcon,
    Separator,
    Spinner,
    TooltipTrigger,
    XStack,
    YStack,
} from 'smoothie';

import useGetAccountsForMembership from '../../../../banking/banking/hooks/useGetAccountsForMembership';
import { GroveSimpleModal } from '../../../../common/components/GroveSimpleModal';
import { adminUserAtom } from '../../../entityAtoms';
import {
    AdminUserRole,
    type IncomeSplitExecution,
    type IncomeSplitExecutionRow,
    type MembershipDetails,
} from '../../../entityTypes';
import { useIncomeSplitExecutions } from '../../hooks/useGetIncomeSplitExecutions';
import {
    type IncomeSplitExecutionHistoryItemDto,
    useIncomeSplitOverview,
} from '../../hooks/useGetIncomeSplitOverview';
import IncomeSplitExecutionDevInfoModal from './IncomeSplitExecutionDetailModal';
import IncomeSplitExecutionRowDetailModal from './IncomeSplitExecutionRowDetailModal';
import { IncomeSplitSimulateModal } from './IncomeSplitSimulateModal';

interface IncomeSplitV2TabPanelProps {
    membershipDetails: MembershipDetails;
}

export const IncomeSplitV2TabPanel = ({
    membershipDetails,
}: IncomeSplitV2TabPanelProps) => {
    const [selectedExecution, setSelectedExecution] =
        useState<IncomeSplitExecution | null>(null);
    const [selectedRowExecution, setSelectedRowExecution] =
        useState<IncomeSplitExecution | null>(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isRowDetailModalOpen, setIsRowDetailModalOpen] = useState(false);
    const [isSimulateModalOpen, setIsSimulateModalOpen] = useState(false);
    const [isAccountDetailsOpen, setIsAccountDetailsOpen] = useState(false);

    const adminUser = useAtomValue(adminUserAtom);
    const isSuperAdmin = adminUser?.role === AdminUserRole.SUPERADMIN;

    const { executions, isLoading } = useIncomeSplitExecutions(
        membershipDetails.id,
    );

    const { data: overviewData, isLoading: isLoadingOverview } =
        useIncomeSplitOverview(membershipDetails.id);

    // Get current month data (prioritize month of most recent execution, then current calendar month, then most recent in history)
    const currentMonthData = useMemo(() => {
        if (!overviewData?.history || overviewData.history.length === 0) {
            return null;
        }

        // First, try to match the month of the most recent execution
        if (executions && executions.length > 0) {
            const mostRecentExecution = executions[0]; // Already sorted by date descending

            if (mostRecentExecution) {
                const executionDate = new Date(
                    mostRecentExecution.processStartedAt,
                );
                const executionMonth = `${executionDate.getFullYear()}-${String(executionDate.getMonth() + 1).padStart(2, '0')}`;

                const executionMonthItem = overviewData.history.find(
                    (item) => item.month === executionMonth,
                );

                if (executionMonthItem) {
                    return executionMonthItem;
                }
            }
        }

        // Second, try to find current calendar month
        const now = new Date();
        const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

        const currentMonthItem = overviewData.history.find(
            (item) => item.month === currentMonth,
        );

        if (currentMonthItem) {
            return currentMonthItem;
        }

        // Finally, get the most recent month from history
        const sortedHistory = [...overviewData.history].sort((a, b) => {
            return b.month.localeCompare(a.month);
        });
        return sortedHistory[0];
    }, [overviewData, executions]);

    // Helper function to find execution from overview data
    const findExecutionFromOverview = useMemo(() => {
        return (
            executionId: string | null,
        ): IncomeSplitExecutionHistoryItemDto | null => {
            if (
                executionId == null ||
                executionId === '' ||
                !overviewData?.history
            ) {
                return null;
            }

            for (const monthData of overviewData.history) {
                const execution = monthData.executions.find(
                    (exec) => exec.id === executionId,
                );
                if (execution) {
                    return execution;
                }
            }

            return null;
        };
    }, [overviewData]);

    const { data: depositAccounts, isLoading: isLoadingDepositAccounts } =
        useGetAccountsForMembership({
            membershipId: membershipDetails.id,
        });

    const { holisticSummary, incomeSplitConfig: tiedRulesIncomeSplitConfig } =
        membershipDetails;

    const holisticAccounts = Object.values(holisticSummary ?? {}).flatMap(
        (section) => section.accounts ?? [],
    );

    const bankAccounts =
        holisticAccounts as unknown as BankAccountDisplayDetail[];
    const externalAccounts = (depositAccounts ?? []).filter(
        (account) => account.isExternal,
    );

    const incomeSplitSourceAccountIds = new Set(
        (tiedRulesIncomeSplitConfig?.accounts ?? []).map(
            (account) => account.accountId,
        ),
    );
    const sourceAccounts = holisticAccounts.filter((account) =>
        incomeSplitSourceAccountIds.has(account.bankAccountId ?? ''),
    );

    const tiedRulesIncomeSplitStatus =
        tiedRulesIncomeSplitConfig?.status ?? PaycheckRoutingStatus.DELETED;

    const executionsData = useMemo(() => {
        if (!executions) return [];

        return executions
            .sort(
                (a, b) =>
                    new Date(b.processStartedAt).getTime() -
                    new Date(a.processStartedAt).getTime(),
            )
            .map<IncomeSplitExecutionRow>((execution) => {
                const {
                    id,
                    processStartedAt,
                    webhookPostedAt,
                    amount,
                    transactionDescription,
                    executionStatus,
                    successfulTransfers,
                    failedTransfers,
                    sourceSyncteraAccountId,
                    emails,
                } = execution;

                // Look up account name using sourceSyncteraAccountId directly in accounts
                let sourceAccountName: string | undefined;
                if (sourceSyncteraAccountId) {
                    // Check holisticAccounts first (may have syncteraAccountId at runtime)
                    const matchedHolisticAccount = holisticAccounts.find(
                        (account) =>
                            (account as { syncteraAccountId?: string })
                                ?.syncteraAccountId === sourceSyncteraAccountId,
                    );
                    if (matchedHolisticAccount) {
                        sourceAccountName = matchedHolisticAccount.name;
                    } else {
                        // Check depositAccounts - id might be syncteraAccountId, or check for syncteraAccountId property at runtime
                        const matchedDepositAccount = depositAccounts?.find(
                            (account) =>
                                account.id === sourceSyncteraAccountId ||
                                (account as { syncteraAccountId?: string })
                                    ?.syncteraAccountId ===
                                    sourceSyncteraAccountId,
                        );
                        if (matchedDepositAccount) {
                            sourceAccountName = matchedDepositAccount.nickname;
                        }
                    }
                }

                // Transaction date is only available in emails for completed executions
                // For pending/skipped executions, emails haven't been sent yet
                // Fallback to webhookPostedAt as an approximation
                const transactionDate =
                    emails.length > 0 &&
                    emails[0]?.transactionDate != null &&
                    emails[0]?.transactionDate !== ''
                        ? new Date(emails[0].transactionDate).toLocaleString()
                        : webhookPostedAt != null && webhookPostedAt !== ''
                          ? new Date(webhookPostedAt).toLocaleString()
                          : 'N/A';

                return {
                    id,
                    date: new Date(processStartedAt).toLocaleString(),
                    transactionDate,
                    amount: centsToFormattedDollars(amount),
                    description: transactionDescription,
                    account: sourceAccountName ?? 'N/A',
                    status: executionStatus,
                    successfulTransfers,
                    failedTransfers,
                    execution,
                };
            });
    }, [executions, holisticAccounts, depositAccounts]);

    const statusSeverity = (status: string) => {
        switch (status) {
            case 'SUCCEEDED':
                return 'success';
            case 'FAILED':
                return 'danger';
            case 'IN_PROGRESS':
                return 'warning';
            default:
                return 'info';
        }
    };

    if (isLoading || isLoadingDepositAccounts) return <Spinner />;

    return (
        <>
            {tiedRulesIncomeSplitConfig ? (
                <XStack gap={16}>
                    <YStack gap={16} flexBasis="40%">
                        <BodyLargeEmphasized>
                            Income Split Configuration
                        </BodyLargeEmphasized>
                        <IncomeSplitDetails
                            incomeSplit={tiedRulesIncomeSplitConfig}
                            totalEligibleAccounts={
                                // TODO: modify isAccountValidAsIncomeSplitSource to work with memberships, so we can properly show 'All' in the copy like we do for the member app
                                sourceAccounts as unknown as BankAccountDisplayDetail[]
                            }
                            icon={EyeIcon}
                            statusBadge={
                                <Badge
                                    value={tiedRulesIncomeSplitStatus}
                                    severity={
                                        tiedRulesIncomeSplitStatus ===
                                        PaycheckRoutingStatus.ACTIVE
                                            ? 'success'
                                            : 'danger'
                                    }
                                />
                            }
                            onIconPress={() => {
                                setIsAccountDetailsOpen(true);
                            }}
                            verifyNumberOfAccountsWatched={false}
                        />

                        <IncomeSplitRulesListNew
                            incomeSplit={formatIncomeSplitPercentagesNew(
                                tiedRulesIncomeSplitConfig,
                            )}
                            bankAccountDetails={bankAccounts}
                            externalAccounts={externalAccounts}
                        />
                        <IncomeSplitEndOfRules />
                        <GroveSimpleModal
                            isOpen={isAccountDetailsOpen}
                            onClose={() => {
                                setIsAccountDetailsOpen(false);
                            }}
                        >
                            <BodyMediumEmphasized
                                textAlign="center"
                                paddingBottom={16}
                            >
                                Income Accounts
                            </BodyMediumEmphasized>
                            <YStack
                                borderRadius={16}
                                backgroundColor="$gray200"
                                separator={<Separator borderColor="$gray500" />}
                            >
                                {sourceAccounts.map((account, i) => (
                                    <Account account={account} key={i} />
                                ))}
                            </YStack>
                        </GroveSimpleModal>
                    </YStack>
                    <YStack flexBasis="60%">
                        <XStack
                            justifyContent="space-between"
                            alignItems="center"
                            marginBottom={16}
                        >
                            <BodyLargeEmphasized>
                                Income Split Executions
                            </BodyLargeEmphasized>
                            {isSuperAdmin && (
                                <Button
                                    variant={ButtonVariant.SECONDARY}
                                    size={ButtonSize.TINY}
                                    text="Simulate"
                                    width="auto"
                                    buttonProps={{ maxWidth: 120 }}
                                    onPress={() => {
                                        setIsSimulateModalOpen(true);
                                    }}
                                />
                            )}
                        </XStack>
                        {!isLoadingOverview &&
                            currentMonthData?.executions != null &&
                            currentMonthData.executions.length > 0 &&
                            currentMonthData.executions.some(
                                (exec) =>
                                    exec.executionFromConfigSourcedFromMoneyMap,
                            ) &&
                            currentMonthData.progress != null && (
                                <YStack
                                    gap={8}
                                    padding={12}
                                    backgroundColor="$gray200"
                                    borderRadius={8}
                                    marginBottom={16}
                                >
                                    <BodyMediumEmphasized>
                                        Current Month Totals (
                                        {(() => {
                                            const parts =
                                                currentMonthData.month.split(
                                                    '-',
                                                );
                                            const year = Number(parts[0]);
                                            const month = Number(parts[1]);
                                            if (isNaN(year) || isNaN(month)) {
                                                return currentMonthData.month;
                                            }
                                            const displayDate = new Date(
                                                year,
                                                month - 1,
                                                1,
                                            );
                                            return displayDate.toLocaleString(
                                                'default',
                                                {
                                                    month: 'long',
                                                    year: 'numeric',
                                                },
                                            );
                                        })()}
                                        )
                                    </BodyMediumEmphasized>
                                    <IncomeSplitProgressOverview
                                        progress={{
                                            bills: {
                                                percentage:
                                                    currentMonthData.progress
                                                        .bills.percentage,
                                                cents: currentMonthData.progress
                                                    .bills.cents,
                                            },
                                            spend: {
                                                percentage:
                                                    currentMonthData.progress
                                                        .spend.percentage,
                                                cents: currentMonthData.progress
                                                    .spend.cents,
                                            },
                                            goals: {
                                                percentage:
                                                    currentMonthData.progress
                                                        .goals.percentage,
                                                cents: currentMonthData.progress
                                                    .goals.cents,
                                            },
                                        }}
                                    />
                                </YStack>
                            )}
                        <DataTable
                            value={executionsData}
                            tableStyle={{ width: '100%' }}
                            size="small"
                            emptyMessage="No income split executions yet"
                            showHeaders={executionsData.length > 0}
                            selectionMode="single"
                            scrollable
                            scrollHeight="1000px"
                            onRowSelect={(e: {
                                data: IncomeSplitExecutionRow;
                            }) => {
                                const { data } = e;
                                setSelectedRowExecution(data.execution);
                                setIsRowDetailModalOpen(true);
                            }}
                        >
                            <Column
                                header=""
                                style={{ width: '40px' }}
                                body={(rowData: IncomeSplitExecutionRow) => {
                                    const overviewExec =
                                        findExecutionFromOverview(
                                            rowData.execution.id,
                                        );
                                    const hasProgress =
                                        overviewExec?.executionFromConfigSourcedFromMoneyMap ??
                                        false;

                                    if (!hasProgress) {
                                        return null;
                                    }

                                    return (
                                        <XStack
                                            justifyContent="center"
                                            alignItems="center"
                                        >
                                            <Icon
                                                icon={MoneyMapIcon}
                                                size={16}
                                                color="$sweetViolet700"
                                            />
                                        </XStack>
                                    );
                                }}
                            />
                            <Column field="date" header="Execution Date" />
                            <Column
                                field="transactionDate"
                                header="Transaction Date"
                            />
                            <Column field="account" header="Account" />
                            <Column field="amount" header="Amount" />
                            <Column field="description" header="Description" />
                            <Column
                                field="status"
                                header="Status"
                                body={(rowData: IncomeSplitExecutionRow) => {
                                    const executionStatus = rowData.status;

                                    // Check if execution has any transactions
                                    const transactions =
                                        rowData.execution.transactions;
                                    const hasTransactions =
                                        Array.isArray(transactions) &&
                                        transactions.length > 0;

                                    // If status is SUCCEEDED → show COMPLETED (green)
                                    if (executionStatus === 'SUCCEEDED') {
                                        return (
                                            <Badge
                                                value="COMPLETED"
                                                severity="success"
                                            />
                                        );
                                    }

                                    // If status is FAILED → check if it has transactions
                                    if (executionStatus === 'FAILED') {
                                        if (hasTransactions) {
                                            // Failed but has transactions → completed with warning icon
                                            return (
                                                <XStack
                                                    gap={6}
                                                    alignItems="center"
                                                >
                                                    <Badge
                                                        value="COMPLETED"
                                                        severity="success"
                                                    />
                                                    <TooltipTrigger tooltipContent="one or more transfers failed">
                                                        <Icon
                                                            icon={
                                                                AlertCircleIcon
                                                            }
                                                            size={16}
                                                            color="$red900"
                                                        />
                                                    </TooltipTrigger>
                                                </XStack>
                                            );
                                        } else {
                                            // Failed with no transactions → catastrophic failure
                                            return (
                                                <Badge
                                                    value="FAILED"
                                                    severity="danger"
                                                />
                                            );
                                        }
                                    }

                                    // For any other status, show the original status
                                    return (
                                        <Badge
                                            value={rowData.status}
                                            severity={statusSeverity(
                                                rowData.status,
                                            )}
                                        />
                                    );
                                }}
                            />
                            <Column
                                header=""
                                body={(rowData: IncomeSplitExecutionRow) => {
                                    return (
                                        <div
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                            }}
                                            onKeyDown={(
                                                e: React.KeyboardEvent,
                                            ) => {
                                                if (
                                                    e.key === 'Enter' ||
                                                    e.key === ' '
                                                ) {
                                                    e.stopPropagation();
                                                }
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <IconButton
                                                variant="tertiary"
                                                icon={CodeIcon}
                                                onPress={() => {
                                                    setSelectedExecution(
                                                        rowData.execution,
                                                    );
                                                    setIsDetailModalOpen(true);
                                                }}
                                            />
                                        </div>
                                    );
                                }}
                            />
                        </DataTable>
                    </YStack>
                </XStack>
            ) : (
                <YStack gap={16}>
                    <XStack
                        justifyContent="space-between"
                        alignItems="center"
                        marginBottom={16}
                    >
                        <BodyLargeEmphasized>
                            Income Split Executions
                        </BodyLargeEmphasized>
                        {isSuperAdmin && (
                            <Button
                                variant={ButtonVariant.SECONDARY}
                                size={ButtonSize.TINY}
                                text="Simulate"
                                width="auto"
                                buttonProps={{ maxWidth: 120 }}
                                onPress={() => {
                                    setIsSimulateModalOpen(true);
                                }}
                            />
                        )}
                    </XStack>
                    {!isLoadingOverview &&
                        currentMonthData?.executions != null &&
                        currentMonthData.executions.length > 0 &&
                        currentMonthData.executions.some(
                            (exec) =>
                                exec.executionFromConfigSourcedFromMoneyMap,
                        ) &&
                        currentMonthData.progress != null && (
                            <YStack
                                gap={8}
                                padding={12}
                                backgroundColor="$gray200"
                                borderRadius={8}
                                marginBottom={16}
                            >
                                <BodyMediumEmphasized>
                                    Current Month Totals (
                                    {(() => {
                                        const parts =
                                            currentMonthData.month.split('-');
                                        const year = Number(parts[0]);
                                        const month = Number(parts[1]);
                                        if (isNaN(year) || isNaN(month)) {
                                            return currentMonthData.month;
                                        }
                                        const displayDate = new Date(
                                            year,
                                            month - 1,
                                            1,
                                        );
                                        return displayDate.toLocaleString(
                                            'default',
                                            {
                                                month: 'long',
                                                year: 'numeric',
                                            },
                                        );
                                    })()}
                                    )
                                </BodyMediumEmphasized>
                                <IncomeSplitProgressOverview
                                    progress={{
                                        bills: {
                                            percentage:
                                                currentMonthData.progress.bills
                                                    .percentage,
                                            cents: currentMonthData.progress
                                                .bills.cents,
                                        },
                                        spend: {
                                            percentage:
                                                currentMonthData.progress.spend
                                                    .percentage,
                                            cents: currentMonthData.progress
                                                .spend.cents,
                                        },
                                        goals: {
                                            percentage:
                                                currentMonthData.progress.goals
                                                    .percentage,
                                            cents: currentMonthData.progress
                                                .goals.cents,
                                        },
                                    }}
                                />
                            </YStack>
                        )}
                    <DataTable
                        value={executionsData}
                        tableStyle={{ width: '100%' }}
                        size="small"
                        emptyMessage="No income split executions yet"
                        showHeaders={executionsData.length > 0}
                        selectionMode="single"
                        scrollable
                        scrollHeight="500px"
                        onRowSelect={(e: { data: IncomeSplitExecutionRow }) => {
                            const { data } = e;
                            setSelectedRowExecution(data.execution);
                            setIsRowDetailModalOpen(true);
                        }}
                    >
                        <Column
                            header=""
                            style={{ width: '40px' }}
                            body={(rowData: IncomeSplitExecutionRow) => {
                                const overviewExec = findExecutionFromOverview(
                                    rowData.execution.id,
                                );
                                const hasProgress =
                                    overviewExec?.executionFromConfigSourcedFromMoneyMap ??
                                    false;

                                if (!hasProgress) {
                                    return null;
                                }

                                return (
                                    <XStack
                                        justifyContent="center"
                                        alignItems="center"
                                    >
                                        <Icon
                                            icon={MoneyMapIcon}
                                            size={16}
                                            color="$gray1000"
                                            style={{ opacity: 0.6 }}
                                        />
                                    </XStack>
                                );
                            }}
                        />
                        <Column field="date" header="Execution Date" />
                        <Column
                            field="transactionDate"
                            header="Transaction Date"
                        />
                        <Column field="account" header="Account" />
                        <Column field="amount" header="Amount" />
                        <Column field="description" header="Description" />
                        <Column
                            field="status"
                            header="Status"
                            body={(rowData: IncomeSplitExecutionRow) => {
                                const executionStatus = rowData.status;

                                // Check if execution has any transactions
                                const transactions =
                                    rowData.execution.transactions;
                                const hasTransactions =
                                    Array.isArray(transactions) &&
                                    transactions.length > 0;

                                // If status is SUCCEEDED → show COMPLETED (green)
                                if (executionStatus === 'SUCCEEDED') {
                                    return (
                                        <Badge
                                            value="COMPLETED"
                                            severity="success"
                                        />
                                    );
                                }

                                // If status is FAILED → check if it has transactions
                                if (executionStatus === 'FAILED') {
                                    if (hasTransactions) {
                                        // Failed but has transactions → completed with warning icon
                                        return (
                                            <XStack gap={6} alignItems="center">
                                                <Badge
                                                    value="COMPLETED"
                                                    severity="success"
                                                />
                                                <TooltipTrigger tooltipContent="one or more transfers failed">
                                                    <Icon
                                                        icon={AlertCircleIcon}
                                                        size={16}
                                                        color="$red900"
                                                    />
                                                </TooltipTrigger>
                                            </XStack>
                                        );
                                    } else {
                                        // Failed with no transactions → catastrophic failure
                                        return (
                                            <Badge
                                                value="FAILED"
                                                severity="danger"
                                            />
                                        );
                                    }
                                }

                                // For any other status, show the original status
                                return (
                                    <Badge
                                        value={rowData.status}
                                        severity={statusSeverity(
                                            rowData.status,
                                        )}
                                    />
                                );
                            }}
                        />
                        <Column
                            header=""
                            body={(rowData: IncomeSplitExecutionRow) => {
                                return (
                                    <div
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                        }}
                                        onKeyDown={(e: React.KeyboardEvent) => {
                                            if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                            ) {
                                                e.stopPropagation();
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <IconButton
                                            variant="tertiary"
                                            icon={CodeIcon}
                                            onPress={() => {
                                                setSelectedExecution(
                                                    rowData.execution,
                                                );
                                                setIsDetailModalOpen(true);
                                            }}
                                        />
                                    </div>
                                );
                            }}
                        />
                    </DataTable>
                </YStack>
            )}
            <IncomeSplitExecutionDevInfoModal
                execution={selectedExecution}
                isOpen={isDetailModalOpen}
                onClose={() => {
                    setIsDetailModalOpen(false);
                    setSelectedExecution(null);
                }}
                membershipDetails={membershipDetails}
            />
            <IncomeSplitExecutionRowDetailModal
                execution={selectedRowExecution}
                overviewExecution={findExecutionFromOverview(
                    selectedRowExecution?.id ?? null,
                )}
                overviewData={overviewData}
                isOpen={isRowDetailModalOpen}
                onClose={() => {
                    setIsRowDetailModalOpen(false);
                    setSelectedRowExecution(null);
                }}
                membershipDetails={membershipDetails}
            />
            <IncomeSplitSimulateModal
                isOpen={isSimulateModalOpen}
                onClose={() => {
                    setIsSimulateModalOpen(false);
                }}
                accounts={depositAccounts ?? []}
            />
        </>
    );
};
